<template>
  <div v-if="visible">
    <div
      v-if="isListView"
      class="tw:cursor-pointer"
      data-testid="refresh-new-mentions-button"
      @click="onClick"
    >
      <QIcon
        class="tw:mr-2"
        name="ion-md-refresh"
        size="20px"
      />
      <I18nT
        keypath="mention_stream_notification_watcher.refresh_new_mentions_button"
        scope="global"
        tag="span"
      >
        <template #mentionCount>
          <strong>{{ formattedMentionCount }}</strong>
        </template>
      </I18nT>
    </div>
    <div
      v-if="!isListView && mentionCountForStream"
      class="tw:shrink-0 tw:cursor-pointer tw:bg-amber-300 tw:flex tw:px-3 tw:py-2 tw:font-bold tw:text-sm tw:items-center tw:justify-center"
      data-testid="new-mention-count-button"
      @click="onClick"
    >
      <QIcon
        class="tw:mr-2"
        name="ion-flash"
        size="23px"
      />
      <span v-if="!currentUser.auto_refresh_streams">
        {{
          getLocaleText(
            "mention_stream_notification_watcher.new_mention_count_with_duration_button",
            {
              mentionCount: formattedMentionCount,
              duration: parseDate,
            }
          )
        }}
      </span>
      <span v-else>
        {{
          $t("mention_stream_notification_watcher.new_mention_count_button", {
            mentionCount: formattedMentionCount,
          })
        }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, watch } from "vue";
import { I18nT } from "vue-i18n";

import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import { fromNow } from "shared/helpers/date";
import { prettyNumber } from "shared/helpers/number";
import { useMentionCountsStore } from "shared/stores/mentionCounts";
import { useUserStore } from "shared/stores/user";
import type { Nullable, Stream } from "shared/types";

interface MentionStreamNotificationWatcherEmits {
  "auto-update": [];
  update: [number];
}

interface MentionStreamNotificationWatcherProps {
  isListView?: boolean;
  stream: Stream;
}

const props = defineProps<MentionStreamNotificationWatcherProps>();

const emit = defineEmits<MentionStreamNotificationWatcherEmits>();

const mentionCountsStore = useMentionCountsStore();
const { mentionCount, streams } = storeToRefs(mentionCountsStore);

const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

const mentionCountForStream = computed<number>(() =>
  mentionCount.value(props.stream.id)
);

const formattedMentionCount = computed<string | number>(() =>
  prettyNumber(mentionCountForStream.value)
);

const parseDate = computed<Nullable<string>>(() => {
  const refreshedAt = props.stream.refreshed_at;

  if (mentionCountForStream.value && refreshedAt) {
    return fromNow(String(refreshedAt));
  }

  return null;
});

const visible = computed<boolean>(
  () =>
    props.isListView || (!props.isListView && mentionCountForStream.value > 0)
);

// If auto refresh is on, automatically load mentions when count > 0. The
// listener decides whether it can act right now (it declines while the user is
// scrolled down mid-list); we re-emit on every count change so a declined
// refresh is retried rather than lost. `immediate` covers mounting into a
// stream whose count was already fetched.
watch(
  streams,
  () => {
    if (
      currentUser.value.auto_refresh_streams &&
      mentionCountForStream.value > 0
    ) {
      emit("auto-update");
    }
  },
  { deep: true, immediate: true }
);

function onClick(): void {
  track("Clicked To See New Mentions", {
    isListView: props.isListView,
    streamId: props.stream.id,
  });

  emit("update", Date.now());
}
</script>
